import mailFolderRouteHandler from './mailFolderRouteHandler';
import mailCategoryRouteHandler from './mailCategoryRouteHandler';
import mailGroupFilesRouteHandler from './mailGroupFilesRouteHandler';
import {
    mailGroupFilesSPCleanupRouteHandler,
    mailGroupFilesSXSNavigationRouteHandler,
    mailGroupFilesSxSCleanupRouteHandler,
} from './mailGroupFilesFilePreviewRouteHandlers';
import mailGroupRouteHandler from './mailGroupRouteHandler';
import mailRowRouteHandler, { mailRowFolderRouteHandler } from './mailRowRouteHandler';
import mailRowCleanupRouteHandler from './mailRowCleanupRouteHandler';
import { mailSxSCleanupRouteHandler, mailSxSNavigationRoutehandler } from './mailSxSRouteHandler';
import { inClientStoreRouteHandler } from 'owa-addins-marketplace-routing';
import personaRouteHandler from './personaRouteHandler';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { initializeDomainConnectRoutes } from 'owa-domain-connect';
import { isGroupsEnabled } from 'owa-account-capabilities/lib/isGroupsEnabled';
import { initializeOptionRoutes } from 'owa-options-router';
import {
    mailComposeCleanupRouteHandler,
    mailComposeNavigationRouteHandler,
    mailComposeBizChatRouteHandler,
} from './mailComposeRouteHandler';
import mailReadRouteHandler from './mailReadRouteHandler';
import type { RegisterRouteFunction } from 'owa-router';
import { mailHelpPaneRouteHandler } from './mailHelpPaneRouteHandler';
import searchRouteHandler, { searchCleanupRouteHandler } from './searchRouteHandler';
import changePhotoRouteHandler from './changePhotoRouteHandler';
import mailGroupFolderRouteHandler, {
    mailGroupFolderRowRouteHandler,
} from './mailGroupFolderRouteHandler';
import feedbackPaneRouteHandler from './feedbackPaneRouteHandler';
import mailModuleRouteHandler from './mailModuleRouteHandler';
import addAccountDialogRouteHandler from './addAccountDialogRouteHandler';
import webapplinkTestRouteHandler from './webapplinkTestRouteHandler';
import { mailCopilotNavigationRouteHandler } from './mailCopilotRouteHandler';
import mailOptionsRouteHandler from './mailOptionsRouteHandler';

export function initializeMailRoutes(registerMailRoute: RegisterRouteFunction): Promise<void> {
    // Note:'applink' routes are used for Monarch deep linking [via standard https urls], with fallback to OWA

    registerMailRoute('/help', mailHelpPaneRouteHandler);
    registerMailRoute('/feedback', feedbackPaneRouteHandler);
    registerMailRoute('/addaccount', addAccountDialogRouteHandler);

    registerMailAndApplinkRoute(
        registerMailRoute,
        '/compose',
        mailComposeNavigationRouteHandler,
        mailComposeCleanupRouteHandler
    );
    registerMailAndApplinkRoute(
        registerMailRoute,
        '/compose/:draftId',
        mailComposeNavigationRouteHandler,
        mailComposeCleanupRouteHandler
    );
    registerMailRoute('/applink/:routeKey/compose', mailComposeBizChatRouteHandler); // keep original route for bizchat compose which differs from current patterns

    registerMailAndApplinkRoute(registerMailRoute, '/read/:itemId', mailReadRouteHandler);

    registerMailRoute(
        '/search/id/:rowId/sxs/:itemId/:attachmentId',
        mailSxSNavigationRoutehandler,
        searchCleanupRouteHandler
    );
    registerMailRoute(
        '/search/id/:rowId/sxs/:attachmentId',
        mailSxSNavigationRoutehandler,
        searchCleanupRouteHandler
    );
    registerMailRoute('/search/id/:rowId', mailRowRouteHandler, searchCleanupRouteHandler);
    registerMailRoute('/search', searchRouteHandler, searchCleanupRouteHandler);
    registerMailRoute('/mail/id/:rowId', mailRowFolderRouteHandler);

    initializeGroupRoutes(registerMailRoute);
    initializeOptionRoutes(registerMailRoute);
    initializeDomainConnectRoutes(registerMailRoute);

    registerMailRoute('/persons/:favoriteNodeId', personaRouteHandler);

    registerMailRoute('/category/:categoryId', mailCategoryRouteHandler);
    registerMailRoute('/inclientstore', inClientStoreRouteHandler);
    registerMailRoute('/inclientstore/assetid/:assetId', inClientStoreRouteHandler);
    registerMailRoute(
        '/inclientstore/assetid/:assetId/campaignid/:campaignId/providerid/:providerId',
        inClientStoreRouteHandler
    );
    registerMailRoute('/changephoto', changePhotoRouteHandler);

    // Route to open prompt within Copilot
    registerMailAndApplinkRoute(registerMailRoute, '/copilot', mailCopilotNavigationRouteHandler);
    registerMailAndApplinkRoute(
        registerMailRoute,
        '/copilot/:deeplink',
        mailCopilotNavigationRouteHandler
    );

    // Route to open mail settings
    registerMailAndApplinkRoute(registerMailRoute, '/settings', mailOptionsRouteHandler);
    registerMailAndApplinkRoute(registerMailRoute, '/settings/:optionKey', mailOptionsRouteHandler);

    registerMailRoute(
        '/:folderId/id/:rowId/sxs/:itemId/:attachmentId',
        mailSxSNavigationRoutehandler,
        mailSxSCleanupRouteHandler
    );
    registerMailRoute(
        '/:folderId/id/:rowId/sxs/:attachmentId',
        mailSxSNavigationRoutehandler,
        mailSxSCleanupRouteHandler
    );

    registerMailRoute(
        '/:folderId/compose/:draftId',
        mailComposeNavigationRouteHandler,
        mailComposeCleanupRouteHandler
    );

    registerMailRoute(
        '/:folderId/compose',
        mailComposeNavigationRouteHandler,
        mailComposeCleanupRouteHandler
    );

    registerMailRoute(
        '/:folderId/id/:rowId',
        mailRowFolderRouteHandler,
        mailRowCleanupRouteHandler
    );

    registerMailRoute('/:folderId', mailFolderRouteHandler, mailRowCleanupRouteHandler);

    if (isGlobalFeatureEnabled('nh-webapplinkTestPage')) {
        registerMailAndApplinkRoute(registerMailRoute, '/test', webapplinkTestRouteHandler);
    }

    registerMailAndApplinkRoute(registerMailRoute, '', mailModuleRouteHandler);

    return Promise.resolve();
}

// Helper to register both normal and 'applink' routes
function registerMailAndApplinkRoute(
    registerMailRoute: RegisterRouteFunction,
    route: string,
    handler: Parameters<RegisterRouteFunction>[1],
    cleanupHandler?: Parameters<RegisterRouteFunction>[2]
) {
    registerMailRoute(route, handler, cleanupHandler);
    const normalizedRoute = route.startsWith('/') ? route : `/${route}`;
    registerMailRoute(`/applink${normalizedRoute}`, handler, cleanupHandler);
    registerMailRoute(`/:routeKey/applink${normalizedRoute}`, handler, cleanupHandler);
}

function initializeGroupRoutes(registerMailRoute: RegisterRouteFunction) {
    if (isGroupsEnabled()) {
        registerMailRoute('/group/:groupDomain/:groupAlias', mailGroupRouteHandler);
        registerMailRoute('/group/:groupDomain/:groupAlias/email', mailGroupRouteHandler);
        registerMailRoute('/group/:groupDomain/:groupAlias/email/id/:rowId', mailGroupRouteHandler);
        registerMailRoute(
            '/group/:groupDomain/:groupAlias/action/:actionId',
            mailGroupRouteHandler
        );

        if (isGlobalFeatureEnabled('grp-loadFolders')) {
            registerMailRoute(
                '/group/:groupDomain/:groupAlias/email/:folderId',
                mailGroupFolderRouteHandler
            );
            registerMailRoute(
                '/group/:groupDomain/:groupAlias/email/:folderId/id/:rowId',
                mailGroupFolderRowRouteHandler
            );
        }

        initializeGroupFilesRoutes(registerMailRoute);
    }
}

function initializeGroupFilesRoutes(registerMailRoute: RegisterRouteFunction) {
    // Order matters here
    registerMailRoute(
        '/group/:groupDomain/:groupAlias/files/sxs/sp/:spFileId/?((\\w|.)*)',
        mailGroupFilesSXSNavigationRouteHandler,
        mailGroupFilesSPCleanupRouteHandler
    );

    registerMailRoute(
        '/group/:groupDomain/:groupAlias/files/sxs/attachment/:attachmentId/?((\\w|.)*)',
        mailGroupFilesSXSNavigationRouteHandler,
        mailGroupFilesSxSCleanupRouteHandler
    );

    registerMailRoute(
        '/group/:groupDomain/:groupAlias/files/?((\\w|.)*)',
        mailGroupFilesRouteHandler
    );
}
